import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "ko", label: "한" },
  { code: "zh", label: "中" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const navLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.about"), path: "/about" },
    { label: t("nav.services"), path: "/services" },
    { label: t("nav.faqs"), path: "/faqs" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  const currentLang = i18n.language?.slice(0, 2) || "en";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "-translate-y-full opacity-0 pointer-events-none py-3"
          : "bg-transparent py-6 translate-y-0 opacity-100"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo + Main Site Button */}
        <div className="flex items-center gap-5">
          <Link to="/" className="flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/85331cf6-12f6-47c1-b191-4118410918c6/33a775d9-6a64-4097-ad05-d95995103288_1_SVG.svg?v=351209cddd3e60767b0d050b6483fc04"
              alt="Wills & Trust - Law Office of Hong-min Jun"
              className="h-48 w-auto object-contain"
            />
          </Link>

          {/* Divider */}
          <span className="hidden lg:block w-px h-7 bg-[#D2D4DE]" />

          {/* Main Site Link */}
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-1.5 group whitespace-nowrap cursor-pointer"
          >
            <span
              className="text-[#74758A] group-hover:text-[#1E202A] transition-colors duration-300"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12px", letterSpacing: "0.06em" }}
            >
              Visit Law Office of Hong-min Jun&apos;s Main Site
            </span>
            <span className="w-4 h-4 flex items-center justify-center text-[#B8942A] group-hover:translate-x-0.5 transition-transform duration-300">
              <i className="ri-arrow-right-up-line text-xs" />
            </span>
          </a>
        </div>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 relative group whitespace-nowrap ${
                    isActive ? "text-[#8A7A6A]" : "text-[#1E202A]/70 hover:text-[#1E202A]"
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "15px", letterSpacing: "0.04em" }}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-px bg-[#8A7A6A] transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right: Language toggle + CTA */}
        <div className="hidden md:flex items-center gap-4">
          {/* Language toggle */}
          <div className="flex items-center gap-0 border border-[#D2D4DE] rounded-full overflow-hidden">
            {LANGS.map((lang, i) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  currentLang === lang.code
                    ? "bg-[#1E202A] text-[#EDEEF3]"
                    : "text-[#74758A] hover:text-[#1E202A] bg-transparent"
                } ${i > 0 ? "border-l border-[#D2D4DE]" : ""}`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer border border-[#1E202A] text-[#1E202A] hover:bg-[#1E202A] hover:text-[#EDEEF3]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "14px" }}
          >
            {t("nav.consultation")}
          </Link>
        </div>

        {/* Mobile Button */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-[#1E202A] cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          menuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        } bg-[#EDEEF3] border-t border-[#D2D4DE]`}
      >
        <ul className="px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`block text-base py-1 transition-colors ${
                  location.pathname === link.path ? "text-[#8A7A6A]" : "text-[#1E202A]/70 hover:text-[#1E202A]"
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/contact" className="inline-block mt-1 px-6 py-2.5 border border-[#1E202A] text-[#1E202A] rounded-full text-sm whitespace-nowrap">
              {t("nav.consultation")}
            </Link>
          </li>
          {/* Mobile language toggle */}
          <li className="pt-2 border-t border-[#D2D4DE]">
            <div className="flex items-center gap-2">
              {LANGS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border cursor-pointer transition-all ${
                    currentLang === lang.code
                      ? "bg-[#1E202A] text-[#EDEEF3] border-[#1E202A]"
                      : "border-[#D2D4DE] text-[#74758A]"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </li>
        </ul>
      </div>
    </header>
  );
}
